import type React from 'react';
import { ScrollReveal } from '@podus/ui';
import { MdOutlineRemoveRedEye, MdOutlineRocketLaunch, MdOutlineBalance, MdArrowOutward } from 'react-icons/md';
import type { IconType } from 'react-icons';

interface Reason {
    Icon: IconType | (() => React.JSX.Element);
    title: string;
    description: string;
}

/**
 * Tiny inline outline of Nigeria — keeps the icon set self-contained until a
 * real asset lands in /public/assets.
 */
function NigeriaIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M3.5 9.5 L4 6 L7 4.5 L9.5 5 L11 3.5 L13.5 3 L15 4 L17 4 L19 5.5 L20 8 L19 10 L20 12.5 L19 15.5 L17 17 L15 18 L13 19.5 L11 20.5 L9 20 L7 19 L5.5 17 L4.5 14 L3.5 11.5 Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
        </svg>
    );
}

const reasons: Reason[] = [
    {
        Icon: MdOutlineRemoveRedEye,
        title: 'Clarity Over Confusion',
        description: 'Know exactly where your case stands at every stage.',
    },
    {
        Icon: MdOutlineRocketLaunch,
        title: 'Speed Without Chaos',
        description: 'Structured workflows eliminate unnecessary delays.',
    },
    {
        Icon: MdOutlineBalance,
        title: 'Fairness By Design',
        description: 'Every case is routed objectively based on its needs.',
    },
    {
        Icon: NigeriaIcon,
        title: 'Built For Nigeria',
        description: 'Designed to work within real-world systems and constraints.',
    },
];

export default function WhyMultidoor() {
    return (
        <section className="px-4 py-16 sm:py-20 lg:px-10 lg:py-24 xl:px-16 xl:py-28">
            <div className="mx-auto grid max-w-[1920px] gap-12 lg:grid-cols-2 lg:items-center lg:gap-12 xl:gap-16">
                {/* ── Left — heading + reasons grid ── */}
                <div className="min-w-0">
                    <ScrollReveal variant="fade-up" duration={800}>
                        <h2 className="text-[clamp(2rem,3.4vw,3.25rem)] font-extrabold leading-[1.15] text-secondary">
                            Why Podus Multidoor?
                        </h2>
                    </ScrollReveal>

                    <div className="mt-12 grid gap-x-10 gap-y-12 sm:grid-cols-2  lg:mt-20 lg:max-w-[500px] lg:gap-x-16 lg:gap-y-16">
                        {reasons.map(({ Icon, title, description }, i) => (
                            <ScrollReveal key={title} variant="fade-up" delay={i * 80} duration={700}>
                                <div>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFE3D6] text-primary sm:h-14 sm:w-14 lg:h-[60px] lg:w-[60px]">
                                        <Icon size={24} />
                                    </div>
                                    <h3 className="mt-5 text-[clamp(1.25rem,2vw,1.75rem)] font-bold leading-[1.25] text-secondary sm:mt-6">
                                        {title}
                                    </h3>
                                    <p className="mt-2.5 max-w-[280px] text-[clamp(0.8125rem,0.95vw,0.9375rem)] leading-[1.55] text-[#7E8492] sm:mt-3">
                                        {description}
                                    </p>
                                </div>
                            </ScrollReveal>
                        ))}
                    </div>
                </div>

                {/* ── Right — image with overlay card. Hidden below lg per design ── */}
                <ScrollReveal variant="fade-up" delay={150} duration={900} className="hidden min-w-0 lg:block">
                    <div className="relative">
                        <img
                            src="/assets/concentrated-young-african-man-sitting-coworking.png"
                            alt="Person working on a Multidoor case"
                            className="h-[520px] w-full rounded-2xl object-cover xl:h-[600px]"
                        />

                        {/* Overlay card — vertically centered, left-anchored, ~55% of image width */}
                        <div className="absolute left-6 bottom-[10px] w-[60%] rounded-2xl bg-white p-6 xl:left-8 xl:p-7">
                            <p className="text-justify text-[13px] leading-[1.7] text-[#4E4D4D]">
                                Podus Multidoor connects you directly to Nigeria&apos;s Multi-Door Courthouse system,
                                allowing you to seamlessly open and manage cases through mediation, arbitration,
                                conciliation, and other court-recognized dispute resolution methods.
                            </p>
                            <button
                                type="button"
                                className="mt-5 inline-flex items-center gap-2 rounded-full bg-secondary px-5 py-3 text-[13px] font-semibold text-white transition-colors hover:bg-secondary-400"
                            >
                                Get Started by Creating a Case
                                <MdArrowOutward size={16} />
                            </button>
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
}
